import {
  type AnchorHTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
} from 'react';

import { useSegmentsTriggerContext } from './hooks/useTriggerContext.js';
import type {
  AriaButtonProps,
  ButtonAria,
} from '../../buttons/button/types/button-aria.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';

/**
 * Accepted properties for SegmentSelector customized trigger.
 * @public
 */
export type SegmentSelectorCustomTriggerProps = StylingProps &
  DataTestId &
  BehaviorTrackingProps & {
    /** Elements to be displayed in the CustomTrigger. */
    children:
      | ReactNode
      | ((
          customTriggerProps: {
            displayValue: ReactNode;
            isLoading: boolean;
          },
          props:
            | (StylingProps &
                DataTestId &
                (Partial<ButtonAria<AnchorHTMLAttributes<HTMLButtonElement>>> &
                  AriaButtonProps))
            | null,
        ) => ReactElement);
  };

/**
 * A trigger component to customize the SegmentSelector trigger.
 * It applies all necessary props on the first and closest child,
 * which, therefore, should be an interactive element.
 * @experimental
 */
export const CustomTrigger = (props: SegmentSelectorCustomTriggerProps) => {
  const { children, ...remainingProps } = props;
  const {
    triggerProps,
    displayValue,
    isLoading,
    buttonRef,
    showError,
    ...ariaProps
  } = useSegmentsTriggerContext();

  const dtComponentName = triggerProps?.['data-dt-component'];

  if (isValidElement<PropsWithChildren>(children)) {
    return cloneElement(children, {
      ..._mergeProps(
        { ref: buttonRef },
        triggerProps,
        remainingProps,
        ariaProps,
        children.props,
        { 'data-dt-component': dtComponentName },
      ),
    });
  }

  if (typeof children === 'function') {
    return children(
      { displayValue, isLoading },
      _mergeProps({ ref: buttonRef }, ariaProps, triggerProps, remainingProps),
    );
  }

  if (isDev()) {
    // eslint-disable-next-line no-console
    console.warn(
      'When using the CustomTrigger component, you need to render valid children inside it.',
    );
  }

  return null;
};

CustomTrigger['displayName'] = 'SegmentSelector.CustomTrigger';
